/**
 * Manage Tags Dialog
 *
 * Dialog for creating, renaming, and deleting tag definitions.
 */

import { useState } from 'react';
import { Trash2, Pencil, Check, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { TagDefinition } from '../types/image-generator.types';

export interface ManageTagsCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagDefinitions: TagDefinition[];
  onAddTag: (name: string) => void;
  onDeleteTag: (tagId: string) => void;
  onRenameTag: (tagId: string, name: string) => void;
}

function DefinitionList({
  items,
  onRename,
  onDelete,
  newItemPlaceholder,
  onAdd,
}: {
  items: { id: string; name: string }[];
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  newItemPlaceholder: string;
  onAdd: (name: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newValue, setNewValue] = useState('');

  const isDuplicate = (name: string, excludeId?: string) =>
    items.some(
      (item) => item.name.toLowerCase() === name.trim().toLowerCase() && item.id !== excludeId,
    );

  const startEditing = (id: string, currentName: string) => {
    setEditingId(id);
    setEditValue(currentName);
  };

  const confirmEdit = () => {
    if (!editingId || !editValue.trim()) return;
    if (isDuplicate(editValue, editingId)) return;
    onRename(editingId, editValue.trim());
    setEditingId(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleAdd = () => {
    if (!newValue.trim()) return;
    if (isDuplicate(newValue)) return;
    onAdd(newValue.trim());
    setNewValue('');
  };

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2">
          {editingId === item.id ? (
            <>
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmEdit();
                  if (e.key === 'Escape') cancelEdit();
                }}
                className="h-8 text-sm flex-1"
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={confirmEdit}
                className="h-7 w-7"
                disabled={!editValue.trim() || isDuplicate(editValue, item.id)}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={cancelEdit} className="h-7 w-7">
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm flex-1">{item.name}</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => startEditing(item.id, item.name)}
                className="h-7 w-7 text-muted-foreground hover:text-primary"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(item.id)}
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      ))}

      {/* New item input */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
          placeholder={newItemPlaceholder}
          className="h-8 text-sm flex-1"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={handleAdd}
          className="h-7 w-7"
          disabled={!newValue.trim() || isDuplicate(newValue)}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function ManageTagsCategoriesDialog({
  open,
  onOpenChange,
  tagDefinitions,
  onAddTag,
  onDeleteTag,
  onRenameTag,
}: ManageTagsCategoriesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Tags section */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Tags</h3>
            {tagDefinitions.length === 0 && (
              <p className="text-xs text-muted-foreground mb-2">No tags yet.</p>
            )}
            <DefinitionList
              items={tagDefinitions}
              onRename={onRenameTag}
              onDelete={onDeleteTag}
              newItemPlaceholder="New tag name..."
              onAdd={onAddTag}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
